import PropTypes from 'prop-types'
import { Box, SvgIcon, Typography } from '@mui/material'

import Button from '../../button/button.component'
import { BUTTON_SIZE, BUTTON_VARIANTS } from '../../button/button.constants'
import { ALERT_SYSTEM_TYPE_CONFIG } from '../../alerts-system'
import { alertHoverStyles } from '../../alerts-system/components/AlertHover/AlertHover.styles'

import { styles } from './alert-section.styles'

/**
 * Shared wrapper for alert categories in the sidebar dialog.
 *
 * Supports two modes:
 * - "simple": badge + title + description text (no children)
 * - "complex": badge + title + optional count + optional "View all" link + children content
 */
const AlertSection = ({
  type,
  title,
  count,
  description,
  viewAllLabel,
  viewAllHref,
  onViewAll,
  children,
}) => {
  const config = ALERT_SYSTEM_TYPE_CONFIG[type]
  const { Icon, colors } = config
  const isSimple = !children

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <Box sx={alertHoverStyles.iconWrapper(colors.bg)}>
            <SvgIcon component={Icon} inheritViewBox sx={{ color: colors.icon }} />
          </Box>
          <Box>
            <Box sx={styles.titleRow}>
              <Typography typography="header4" color="gray.80">
                {title}
              </Typography>
              {count != null && (
                <>
                  <Typography typography="medium" color="gray.40" sx={styles.dot}>
                    {'•'}
                  </Typography>
                  <Typography typography="medium" color="gray.64">
                    {count}
                  </Typography>
                </>
              )}
            </Box>
            {isSimple && description && (
              <Typography typography="medium" color="gray.64" sx={styles.descriptionText}>
                {description}
              </Typography>
            )}
          </Box>
        </Box>
        {viewAllLabel && (viewAllHref || onViewAll) && (
          <Button
            label={viewAllLabel}
            size={BUTTON_SIZE.SMALL}
            variant={BUTTON_VARIANTS.TEXT}
            href={viewAllHref}
            onClick={onViewAll}
          />
        )}
      </Box>
      {children && <Box sx={styles.content}>{children}</Box>}
    </Box>
  )
}

AlertSection.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  description: PropTypes.string,
  viewAllLabel: PropTypes.string,
  viewAllHref: PropTypes.string,
  onViewAll: PropTypes.func,
  children: PropTypes.node,
}

export default AlertSection
